import React, { useState, useEffect } from 'react';
import { Info, ExternalLink, CheckCircle, AlertCircle } from 'lucide-react';
import { integrationService } from '../../services/integrationService';

const WixForm = ({ formData, setFormData, isSubmitting, handleCreateIntegration }) => {
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [isLoadingBlogs, setIsLoadingBlogs] = useState(false);

  // Function to verify Wix credentials
  const verifyCredentials = async () => {
    if (!formData.site_id || !formData.api_key) {
      return;
    }

    setVerifying(true);
    setVerificationResult(null);

    try {
      const result = await integrationService.verifyWixCredentials({
        site_id: formData.site_id,
        api_key: formData.api_key
      });

      setVerificationResult(result);

      if (result.valid) {
        // Fetch blogs if credentials are valid
        fetchBlogs();
      }
    } catch (error) {
      setVerificationResult({
        valid: false,
        error: error.message || 'Verification failed'
      });
    } finally {
      setVerifying(false);
    }
  };

  // Function to fetch Wix blogs
  const fetchBlogs = async () => {
    if (!formData.site_id || !formData.api_key) {
      return;
    }

    setIsLoadingBlogs(true);

    try {
      const result = await integrationService.getWixBlogs({
        site_id: formData.site_id,
        api_key: formData.api_key
      });

      setBlogs(result.blogs || []);
    } catch (error) {
      console.error('Error fetching Wix blogs:', error);
    } finally {
      setIsLoadingBlogs(false);
    }
  };

  // Reset verification when credentials change
  useEffect(() => {
    setVerificationResult(null);
  }, [formData.site_id, formData.api_key]);

  return (
    <form onSubmit={handleCreateIntegration} className="space-y-6">
      <div>
        <label className="block text-gray-700 font-medium mb-2">
          Integration Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          className="w-full border border-gray-300 rounded-md p-2"
          placeholder="My Wix Site"
          value={formData.name || ''}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            name: e.target.value
          }))}
          required
        />
      </div>

      <div>
        <label className="block text-gray-700 font-medium mb-2">
          Site ID <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type="text"
            className={`w-full border ${verificationResult?.valid === false ? 'border-red-500' : 'border-gray-300'} rounded-md p-2 pr-8`}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            value={formData.site_id || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              site_id: e.target.value
            }))}
            required
          />
          {verificationResult?.valid && (
            <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500" size={18} />
          )}
        </div>
        <p className="text-sm text-gray-600 mt-1">
          You can find your Site ID in the URL of your Wix Dashboard.
        </p>
      </div>

      <div>
        <label className="block text-gray-700 font-medium mb-2">
          API Key <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type="password"
            className={`w-full border ${verificationResult?.valid === false ? 'border-red-500' : 'border-gray-300'} rounded-md p-2 pr-8`}
            placeholder="Enter your Wix API key"
            value={formData.api_key || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              api_key: e.target.value
            }))}
            required
          />
          {verificationResult?.valid && (
            <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500" size={18} />
          )}
        </div>
        <div className="text-sm text-gray-600 mt-1 flex items-center">
          <Info size={16} className="mr-1" />
          <a
            href="https://dev.wix.com/api/rest/getting-started/api-keys"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline flex items-center"
          >
            Learn how to get your Wix API key <ExternalLink size={14} className="ml-1" />
          </a>
        </div>
      </div>

      <div className="mt-4">
        <button
          type="button"
          onClick={verifyCredentials}
          disabled={verifying || !formData.site_id || !formData.api_key}
          className="px-4 py-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 transition font-medium disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          {verifying ? (
            <span className="flex items-center">
              <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full mr-2"></div>
              Verifying...
            </span>
          ) : 'Verify Credentials'}
        </button>

        {verificationResult?.valid && (
          <div className="mt-2 text-green-600 flex items-center">
            <CheckCircle size={16} className="mr-1" />
            <div>
              <p>Credentials verified successfully</p>
              {verificationResult.site && (
                <p className="text-sm">Connected to: {verificationResult.site.name}</p>
              )}
            </div>
          </div>
        )}

        {verificationResult?.valid === false && (
          <div className="mt-2 text-red-600 flex items-center">
            <AlertCircle size={16} className="mr-1" />
            <div>
              <p>{verificationResult.error || 'Verification failed'}</p>
            </div>
          </div>
        )}
      </div>

      {verificationResult?.valid && (
        <>
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              Blog <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                className="w-full border border-gray-300 rounded-md p-2 appearance-none bg-white pr-8"
                value={formData.blog_id || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  blog_id: e.target.value
                }))}
                disabled={!blogs.length || isLoadingBlogs}
                required
              >
                <option value="">Select a blog</option>
                {blogs.map(blog => (
                  <option key={blog.id} value={blog.id}>{blog.title}</option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {isLoadingBlogs && (
                <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-2">
              Publishing Status <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                className="w-full border border-gray-300 rounded-md p-2 appearance-none bg-white pr-8"
                value={formData.publishing_status || 'draft'}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  publishing_status: e.target.value
                }))}
                required
              >
                <option value="published">Publish Immediately</option>
                <option value="draft">Save as Draft</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Choose whether to publish articles immediately or save them as drafts
            </p>
          </div>
        </>
      )}
    </form>
  );
};

export default WixForm;

